import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Newspaper, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Fetches and renders public news articles from Google News RSS for a given
 * resource (bill or legislator) via the matching backend endpoint.
 *
 * Props:
 *  - endpoint: required, e.g. `/bills/{id}/news` or `/legislators/{id}/news`
 *  - title:    card heading. Defaults to "In the News".
 *  - limit:    max articles. Defaults to 10.
 *  - testId:   data-testid prefix.
 */
export default function NewsCard({ endpoint, title = "In the News", limit = 20, initialVisible = 7, testId = "news-card" }) {
  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setArticles(null);
    setError(false);
    axios
      .get(`${API}${endpoint}`, { params: { limit }, timeout: 25000 })
      .then((r) => {
        if (cancelled) return;
        setArticles(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setArticles([]);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint, limit, attempt]);

  return (
    <Card className="p-6 rounded-xl border border-white/10" style={{ background: "var(--whip-surface)" }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Newspaper className="w-6 h-6 text-orange-400" />
        <h2 className="text-2xl font-bold text-white">{title}</h2>
      </div>
      <p className="text-sm text-white/55 mb-4">
        Recent public news coverage. Sourced from{" "}
        <a
          href="https://news.google.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-orange-400"
        >
          Google News
        </a>
        .
      </p>

      {articles === null && (
        <div className="py-6 text-center" data-testid={`${testId}-loading`}>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2" />
          <p className="text-sm text-white/55">Searching news…</p>
        </div>
      )}

      {articles !== null && articles.length === 0 && (
        <div className="py-4 text-sm text-white/55 flex items-center justify-between gap-3" data-testid={`${testId}-empty`}>
          <span>{error ? "News is temporarily unavailable." : "No recent news articles found."}</span>
          <button type="button" onClick={() => setAttempt((a) => a + 1)}
            className="text-xs font-semibold text-orange-300 hover:text-orange-200 border border-orange-500/30 rounded px-2.5 py-1 transition-colors"
            data-testid={`${testId}-retry`}>Retry</button>
        </div>
      )}

      {articles && articles.length > 0 && (
        <>
          <ul className="divide-y divide-white/5">
            {(showAll ? articles : articles.slice(0, initialVisible)).map((a, i) => (
              <li key={(a.link || "") + i} className="py-3" data-testid={`${testId}-item-${i}`}>
                <a
                  href={a.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold text-white group-hover:text-orange-300 leading-snug">
                      {a.title}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-white/40 shrink-0 mt-1" />
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-white/50 flex-wrap">
                    <span className="font-medium text-white/75">{a.source}</span>
                    {a.publishedAt && (
                      <>
                        <span aria-hidden>·</span>
                        <span>
                          {new Date(a.publishedAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </>
                    )}
                  </div>
                </a>
              </li>
            ))}
          </ul>
          {articles.length > initialVisible && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-4 w-full text-sm font-semibold text-orange-300 hover:text-orange-200 hover:bg-orange-500/10 rounded-md py-2 transition-colors"
              data-testid={`${testId}-toggle`}
            >
              {showAll
                ? "Show fewer articles"
                : `Show ${articles.length - initialVisible} more article${articles.length - initialVisible === 1 ? "" : "s"}`}
            </button>
          )}
        </>
      )}
    </Card>
  );
}
